/**
 * Cloud Functions client for the endeeko backend.
 *
 * Reuses the single FirebaseApp from `@/lib/firebase`, so the signed-in user's ID
 * token rides along on every callable automatically. Nothing connects until a
 * service actually calls the backend (see services/api/backend.ts + VITE_USE_BACKEND).
 */
import { getFunctions, connectFunctionsEmulator, type Functions } from 'firebase/functions';
import { getFirestore, connectFirestoreEmulator, type Firestore } from 'firebase/firestore';
import { getStorage, connectStorageEmulator, type FirebaseStorage } from 'firebase/storage';
import { getFirebaseApp } from '@/lib/firebase';

/** Functions live in us-central1 to match the firebase-functions deployment region. */
const REGION = import.meta.env.VITE_FIREBASE_REGION ?? 'us-central1';

let fns: Functions | undefined;
let db: Firestore | undefined;
let storage: FirebaseStorage | undefined;

export { getFirebaseApp };

export function getFns(): Functions {
  if (!fns) {
    fns = getFunctions(getFirebaseApp(), REGION);
    if (import.meta.env.VITE_USE_EMULATOR === 'true') {
      connectFunctionsEmulator(fns, '127.0.0.1', 5001);
    }
  }
  return fns;
}

/**
 * Cloud Firestore handle — used for reference data the frontend reads directly
 * (e.g. the `industries` collection migrated from the legacy RTDB export). Reuses
 * the shared FirebaseApp so the signed-in user's token is honoured by security rules.
 */
export function getDb(): Firestore {
  if (!db) {
    db = getFirestore(getFirebaseApp());
    if (import.meta.env.VITE_USE_EMULATOR === 'true') {
      connectFirestoreEmulator(db, '127.0.0.1', 8080);
    }
  }
  return db;
}

/**
 * Cloud Storage handle — used for direct client uploads (e.g. a supplier's
 * catalog PDF ahead of the `startCatalogImport` callable). Governed by
 * `storage.rules`' `companies/{companyId}/catalog-imports/**` match, which
 * requires the caller to be a member of that company.
 */
export function getStorageInstance(): FirebaseStorage {
  if (!storage) {
    storage = getStorage(getFirebaseApp());
    if (import.meta.env.VITE_USE_EMULATOR === 'true') {
      connectStorageEmulator(storage, '127.0.0.1', 9199);
    }
  }
  return storage;
}
